import { Box, Grid, Typography } from "@mui/material";
import { Modal } from "../../../../../shared/components/ui/Modal";
import { Button } from "../../../../../shared/components/ui/Button";
import { Input } from "../../../../../shared/components/ui/Input";
import { Select } from "../../../../../shared/components/ui/Select";
import ImageInput from "../../../../../shared/components/ui/ImageInput";
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { userAttributesService } from '../../../../../shared/services/userAtributes/userAtributes';
import * as yup from 'yup';

const vehicleTypeOptions = [
  { label: "Carro", value: "carro" },
  { label: "Moto", value: "moto" },
  { label: "Caminhão", value: "caminhao" },
  { label: "Outro", value: "outro" },
];

const initialState = {
  marca: "",
  modelo: "",
  tipo: "carro",
  placa: "",
  ano: "",
  cor: "",
  observacoes: "",
  foto: [] as File[],
};

const vehicleValidationSchema = yup.object().shape({
  marca: yup.string().required('Marca é obrigatória'),
  modelo: yup.string().required('Modelo é obrigatório'),
  placa: yup.string().required('Placa é obrigatória'),
  ano: yup.number().required('Ano é obrigatório').min(1900, 'Ano deve ser maior que 1900'),
});

interface VehicleFormProps {
  openVehicleModal: boolean;
  setOpenVehicleModal: (open: boolean) => void;
  userId: string;
  initialData?: any;
  onSave?: (vehicle: any) => void;
  editMode?: boolean;
}

const VehicleForm = ({ openVehicleModal, setOpenVehicleModal, userId, initialData, onSave, editMode }: VehicleFormProps) => {
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState<any>({});
  const queryClient = useQueryClient();

  // Buscar veículos existentes para determinar o próximo índice
  const { data: existingVehiclesData } = useQuery({
    queryKey: ['user', userId, 'veiculo'],
    queryFn: () => userAttributesService.get(userId, 'veiculo?pattern=true'),
    enabled: !!userId && !editMode,
  });

  React.useEffect(() => {
    if (editMode && initialData) {
      setForm({
        ...initialData,
        foto: []
      });
    } else {
      setForm(initialState);
    }
  }, [editMode, initialData, openVehicleModal]);

  const createVehicleMutation = useMutation({
    mutationFn: async (vehicleData: any) => {
      const formData = new FormData();
      
      formData.append('userId', userId);
      
      // Buscar quantos atributos 'veiculo' já existem para determinar o próximo índice
      try {
        const existingVehicles = await userAttributesService.get(userId, 'veiculo?pattern=true');
        const vehicleCount = existingVehicles?.data?.value?.length || 0;
        
        formData.append('attributes[0].key', 'veiculo');
        formData.append(`attributes[0].value[${vehicleCount}].marca`, vehicleData.marca);
        formData.append(`attributes[0].value[${vehicleCount}].modelo`, vehicleData.modelo);
        formData.append(`attributes[0].value[${vehicleCount}].tipo`, vehicleData.tipo);
        formData.append(`attributes[0].value[${vehicleCount}].placa`, vehicleData.placa);
        formData.append(`attributes[0].value[${vehicleCount}].ano`, vehicleData.ano);
        formData.append(`attributes[0].value[${vehicleCount}].cor`, vehicleData.cor || '');
        formData.append(`attributes[0].value[${vehicleCount}].observacoes`, vehicleData.observacoes || '');
      } catch (error) {
        // Se não conseguir buscar, assume que é o primeiro (índice 0)
        formData.append('attributes[0].key', 'veiculo');
        formData.append('attributes[0].value[0].marca', vehicleData.marca);
        formData.append('attributes[0].value[0].modelo', vehicleData.modelo);
        formData.append('attributes[0].value[0].tipo', vehicleData.tipo);
        formData.append('attributes[0].value[0].placa', vehicleData.placa);
        formData.append('attributes[0].value[0].ano', vehicleData.ano);
        formData.append('attributes[0].value[0].cor', vehicleData.cor || '');
        formData.append('attributes[0].value[0].observacoes', vehicleData.observacoes || '');
      }
      
      // Adicionar arquivo se existir
      if (vehicleData.foto && vehicleData.foto.length > 0 && vehicleData.foto[0] instanceof File) {
        formData.append('file', vehicleData.foto[0]);
      }
      
      const response = await userAttributesService.createWithFile(formData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', userId, 'veiculo?pattern=true'] });
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
      setOpenVehicleModal(false);
      setForm(initialState);
    },
    onError: (error: any) => {
      console.error('Erro ao salvar veículo:', error);
    }
  });

  const validate = async () => {
    try {
      await vehicleValidationSchema.validate(form, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const newErrors: any = {};
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path] = error.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleChange = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev: any) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!(await validate())) return;

    if (editMode && onSave) {
      const vehicleData = { ...form };
      if (vehicleData.foto && vehicleData.foto.length > 0 && typeof vehicleData.foto[0] === 'object' && !(vehicleData.foto[0] instanceof File)) {
        if (vehicleData.foto) {
          if (vehicleData.foto && vehicleData.foto[0]) {
            delete vehicleData.foto[0];
          }
        }
      }
      onSave(vehicleData);
      return;
    }

    createVehicleMutation.mutate(form);
  };

  return (
    <Modal open={openVehicleModal} onClose={() => setOpenVehicleModal(false)} title={editMode ? "Editar Veículo" : "Cadastrar Veículo"}>
      <form onSubmit={handleSubmit}>
        <Box display="flex" flexDirection="column" gap={3}>
          <Box mb={1}>
           
            {editMode && initialData?.imageUrl && (
              <Box mb={2} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Imagem atual:
                </Typography>
                <Box sx={{
                  width: 120,
                  height: 120,
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '2px solid #e0e0e0'
                }}>
                  <img
                    src={initialData.imageUrl}
                    alt="Veículo atual"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </Box>
              </Box>
            )}
            <ImageInput
              key={`vehicle-image-${openVehicleModal}-${editMode}`}
           //   variant="simple"
              label="Foto do Veículo"
              onChange={files => handleChange("foto", files || [])}
              maxFiles={1}
              initialFiles={form.foto || []}
            />
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Input
                label="Marca"
                placeholder="Ex: Toyota"
                value={form.marca}
                onChange={e => handleChange("marca", e.target.value)}
                required
                error={!!errors.marca}
                helperText={errors.marca}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input
                label="Modelo"
                placeholder="Ex: Corolla"
                value={form.modelo}
                onChange={e => handleChange("modelo", e.target.value)}
                required
                error={!!errors.modelo}
                helperText={errors.modelo}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Select
                label="Tipo"
                value={form.tipo}
                onChange={e => handleChange("tipo", e.target.value)}
                fullWidth
                native
              >
                {vehicleTypeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input
                label="Placa"
                placeholder="Ex: ABC1234"
                value={form.placa}
                onChange={e => handleChange("placa", e.target.value)}
                required
                error={!!errors.placa}
                helperText={errors.placa}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input
                label="Ano"
                placeholder="Ex: 2020"
                value={form.ano}
                onChange={e => handleChange("ano", e.target.value)}
                type="number"
                required
                error={!!errors.ano}
                helperText={errors.ano}
                fullWidth
                inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input
                label="Cor"
                placeholder="Ex: Prata"
                value={form.cor}
                onChange={e => handleChange("cor", e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                label="Observações"
                placeholder="Informações adicionais sobre o veículo..."
                value={form.observacoes}
                onChange={e => handleChange("observacoes", e.target.value)}
                multiline
                minRows={3}
                fullWidth
              />
            </Grid>
          </Grid>
          <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
            <Button variant="outlined" onClick={() => setOpenVehicleModal(false)} type="button">
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createVehicleMutation.isPending}
            >
              {createVehicleMutation.isPending ? "Salvando..." : (editMode ? "Atualizar Veículo" : "Salvar Veículo")}
            </Button>
          </Box>
        </Box>
      </form>
    </Modal>
  );
};

export default VehicleForm;